import { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { MessagesListRefContext, SetPendingScrollContext } from './context';
import type { ScrollParams } from './interfaces.context';

// How close (in px) to the bottom of the scroll container the user must be for passive
// auto-scroll (streaming chunks, agent-progress updates) to keep following new content.
const NEAR_BOTTOM_THRESHOLD_PX = 100;

export const ChatScrollContainer = (props: { children: React.ReactNode }) => {
  const [state, setState] = useState<ScrollParams | null>(null);
  const scrollToBottom = useScrollToBottomOfMessages();

  const handleSetPendingScroll = useCallback((params?: ScrollParams) => {
    setState(params || {});
  }, []);

  useLayoutEffect(() => {
    if (state) {
      scrollToBottom(state);

      setState(null);
    }
  }, [scrollToBottom, state]);

  return (
    <SetPendingScrollContext.Provider value={handleSetPendingScroll}>
      {props.children}
    </SetPendingScrollContext.Provider>
  );
};

function useScrollToBottomOfMessages() {
  const [messagesRef] = useContext(MessagesListRefContext);
  const isNearBottomRef = useRef(true);

  // Track whether the user is currently scrolled near the bottom of the scrollable
  // messages container (messagesRef's parent), so passive auto-scroll triggers don't
  // fight a user who has manually scrolled up to read earlier content.
  useEffect(() => {
    const scrollContainer = messagesRef?.parentElement;
    if (!scrollContainer) {
      return;
    }

    const updateIsNearBottom = () => {
      const distanceFromBottom =
        scrollContainer.scrollHeight - scrollContainer.scrollTop - scrollContainer.clientHeight;
      isNearBottomRef.current = distanceFromBottom <= NEAR_BOTTOM_THRESHOLD_PX;
    };

    updateIsNearBottom();
    scrollContainer.addEventListener('scroll', updateIsNearBottom, { passive: true });

    return () => {
      scrollContainer.removeEventListener('scroll', updateIsNearBottom);
    };
  }, [messagesRef]);

  return useCallback(
    (
      { nthFromBottom, force, ...vars }: ScrollParams = {
        behavior: 'auto',
      }
    ) => {
      setTimeout(() => {
        if (!messagesRef) {
          return;
        }
        if (!messagesRef?.childElementCount) {
          return;
        }

        if (!force && !isNearBottomRef.current) {
          return;
        }

        // A passive scroll (stream chunk, response completion) must only ever follow new content
        // downward. The target is an earlier message (e.g. the user's question above a long
        // response); when the user has scrolled past it, `scrollIntoView` would pull the view
        // back up to the start of the response.
        const wouldScrollUp = (target: Element) => {
          const scrollContainer = messagesRef.parentElement;
          if (force || !scrollContainer) {
            return false;
          }
          return target.getBoundingClientRect().top < scrollContainer.getBoundingClientRect().top;
        };

        if (nthFromBottom) {
          const scrollIndex = messagesRef.children.length - nthFromBottom;
          const scrollTarget = messagesRef.children[scrollIndex];
          if (!scrollTarget || wouldScrollUp(scrollTarget)) {
            return;
          }
          scrollTarget.scrollIntoView({ block: 'nearest', inline: 'nearest', ...vars });
          return;
        }

        const childScrollIndex =
          messagesRef.childElementCount >= 3
            ? messagesRef.children.length - 2
            : messagesRef.children.length - 1;

        const lastChild = messagesRef.children[childScrollIndex];

        if (!lastChild || wouldScrollUp(lastChild)) {
          return;
        }

        lastChild.scrollIntoView({ block: 'nearest', inline: 'nearest', ...vars });
      }, 0);
    },
    [messagesRef]
  );
}
